/**
 * Redux Slice for Enabled Modules State
 *
 * Manages which modules are enabled for the current tenant
 * Fetches from API and caches in Redux store
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import djangoClient, { parseDjangoError } from '../../shared/api/djangoClient';

export const fetchEnabledModules = createAsyncThunk(
  'enabledModules/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await djangoClient.get('/api/v1/auth/tenant/modules/');
      const modules = response.data?.modules || [];
      return {
        keys: modules.map((module) => module.module_key),
        // PLT-06: minimum subscription tier per module, from the backend registry.
        tierMin: Object.fromEntries(
          modules
            .filter((module) => module.tier_min)
            .map((module) => [module.module_key, module.tier_min])
        ),
      };
    } catch (error) {
      return rejectWithValue(parseDjangoError(error));
    }
  }
);

const initialState = {
  modules: [], // List of enabled module keys
  tierMin: {}, // module key -> minimum subscription tier (PLT-06)
  isLoading: false,
  error: null,
  lastFetched: null,
};

const enabledModulesSlice = createSlice({
  name: 'enabledModules',
  initialState,
  reducers: {
    /**
     * Manually set enabled modules (for testing or pre-filled data)
     */
    setEnabledModules: (state, action) => {
      state.modules = action.payload;
    },

    /**
     * Add a single module to enabled list
     */
    addModule: (state, action) => {
      if (!state.modules.includes(action.payload)) {
        state.modules.push(action.payload);
      }
    },

    /**
     * Remove a single module from enabled list
     */
    removeModule: (state, action) => {
      state.modules = state.modules.filter((m) => m !== action.payload);
    },

    /**
     * Reset to initial state
     */
    resetEnabledModules: (state) => {
      state.modules = [];
      state.tierMin = {};
      state.isLoading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch enabled modules
      .addCase(fetchEnabledModules.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEnabledModules.fulfilled, (state, action) => {
        state.isLoading = false;
        state.modules = action.payload.keys;
        state.tierMin = action.payload.tierMin;
        state.lastFetched = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchEnabledModules.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setEnabledModules, addModule, removeModule, resetEnabledModules } =
  enabledModulesSlice.actions;

// Selectors
/**
 * Get all enabled module keys
 */
export const selectEnabledModules = (state) => state.enabledModules.modules;

/**
 * Check if a specific module is enabled
 * Returns a function that takes state and returns boolean
 */
export const selectIsModuleEnabled = (moduleKey) => (state) =>
  state.enabledModules.modules.includes(moduleKey);

/**
 * Get multiple modules' enabled status
 */
export const selectAreModulesEnabled = (moduleKeys) => (state) =>
  moduleKeys.every((key) => state.enabledModules.modules.includes(key));

/**
 * Get loading state
 */
export const selectModulesLoading = (state) => state.enabledModules.isLoading;

/**
 * Get error state
 */
export const selectModulesError = (state) => state.enabledModules.error;

/**
 * Get last fetch timestamp
 */
export const selectModulesLastFetched = (state) => state.enabledModules.lastFetched;

/**
 * Minimum subscription tier for a module (null when unknown).
 */
export const selectModuleTierMin = (moduleKey) => (state) =>
  state.enabledModules.tierMin?.[moduleKey] || null;

export default enabledModulesSlice.reducer;
